import React, { useState, useEffect, useCallback } from 'react';
import { collection, updateDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { createUserAsAdmin } from '../utils/createUser';
import './AdminUsersView.css';
import './AdminDetailPages.css';
import StatCards from './StatCards';
import { adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

function AdminUsersView({ adminData }) {
  const [users, setUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [detailUserChildren, setDetailUserChildren] = useState([]);
  const [students, setStudents] = useState([]);
  const [addUserForm, setAddUserForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'Teacher',
    teacherType: 'regular',
    specialtySubjects: []
  });
  const [addUserError, setAddUserError] = useState('');

  const availableSubjects = ["Mathematics", "English", "Science", "Biology", "Chemistry", "Social Studies", "History", "Spanish", "French", "Information Technology", "Physical Education", "Arts", "Agriculture", "Principles of Accounts (POA)", "Principles of Business (POB)", "Building Technology", "Home Economics", "Woodwork", "Reading Comprehension", "Penmanship"];

  // School-scoped cache key builder — shared with AdminAnalyticsView so tabs
  // reuse each other's fetched collections.
  const ck = (p) => `${adminSchoolIds(adminData).join('|') || 'all'}:${p}`;

  const loadUsers = useCallback(async () => {
    console.log('🔥 AdminUsers: Loading users, schoolId:', adminData?.schoolId);
    try {
      let usersData = await dataCacheService.getDocs(ck('users'), collection(db, 'users'));
      if (adminData) {
        const sid = (adminData.schoolId || '').toLowerCase();
        usersData = sid ? usersData.filter(u => (u.schoolId || '').toLowerCase() === sid) : [];
      }
      console.log('🔥 AdminUsers: Users loaded', usersData.length, usersData);
      setUsers(usersData);
    } catch (error) {
      console.error('🔥 AdminUsers: Error loading users:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData?.schoolId]);

  const loadStudents = useCallback(async () => {
    console.log('🔥 AdminUsers: Loading students, schoolId:', adminData?.schoolId);
    try {
      let classesData = await dataCacheService.getDocs(ck('classes'), collection(db, 'classes'));
      if (adminData) {
        const sid = (adminData.schoolId || '').toLowerCase();
        classesData = sid ? classesData.filter(c => (c.schoolId || '').toLowerCase() === sid) : [];
      }
      const perClass = await Promise.all(classesData.map(cls =>
        dataCacheService.getDocs(ck(`classes/${cls.id}/students`), collection(db, 'classes', cls.id, 'students'))
      ));
      const studentsData = perClass.flat().filter(s => s.isActive !== false);
      console.log('🔥 AdminUsers: Students loaded', studentsData.length);
      setStudents(studentsData);
    } catch (error) {
      console.error('🔥 AdminUsers: Error loading students:', error);
    }
  }, [adminData?.schoolId]);

  useEffect(() => {
    loadUsers();
    loadStudents();
  }, [loadUsers, loadStudents]);

  const getFilteredUsers = () => {
    let filtered = users;
    
    if (roleFilter !== 'All') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }
    
    if (searchText) {
      filtered = filtered.filter(user =>
        user.username?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    return filtered;
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), { role: newRole });
      dataCacheService.invalidatePath('users');
      loadUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update user role');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      teacherType: user.teacherType || 'regular',
      specialtySubject: user.specialtySubject || ''
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    try {
      await updateDoc(doc(db, 'users', editingUser.id), {
        teacherType: editForm.teacherType,
        specialtySubject: editForm.teacherType === 'subject' ? editForm.specialtySubject : null
      });
      dataCacheService.invalidatePath('users');
      setEditingUser(null);
      setEditForm({});
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user');
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditForm({});
  };

  const handleAddUser = async () => {
    setAddUserError('');
    
    if (!addUserForm.username || !addUserForm.email || !addUserForm.password) {
      setAddUserError('Please fill in all required fields');
      return;
    }
    
    if (addUserForm.role === 'Teacher' && addUserForm.teacherType === 'subject' && addUserForm.specialtySubjects.length === 0) {
      setAddUserError('At least one specialty subject is required for subject teachers');
      return;
    }
    
    try {
      // Secondary-app auth so creating the account does NOT sign the admin out.
      const uid = await createUserAsAdmin(addUserForm.email, addUserForm.password);

      const nameParts = addUserForm.username.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const userData = {
        id: uid,
        uuid: crypto.randomUUID(),
        username: addUserForm.username,
        firstName,
        lastName,
        email: addUserForm.email,
        role: addUserForm.role,
        teacherType: addUserForm.role === 'Teacher' ? addUserForm.teacherType : null,
        specialtySubjects: addUserForm.role === 'Teacher' && addUserForm.teacherType === 'subject' ? addUserForm.specialtySubjects : [],
        assignedClassIds: [],
        schoolId: adminData?.schoolId || null,
        isActive: true,
        createdAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'users', uid), userData);
      dataCacheService.invalidatePath('users');

      if (addUserForm.role === 'Teacher') {
        const teacherData = {
          id: uid,
          email: addUserForm.email,
          fullName: addUserForm.username,
          teacherType: addUserForm.teacherType,
          specialtySubjects: addUserForm.teacherType === 'subject' ? addUserForm.specialtySubjects : [],
          createdAt: new Date().toISOString(),
          isActive: true,
          schoolId: adminData?.schoolId || null
        };
        await setDoc(doc(db, 'teachers', uid), teacherData);
        dataCacheService.invalidatePath('teachers');
      }

      if (addUserForm.role === 'Parent') {
        // Parent dashboard requires a parents/{uid} doc — without it the
        // account can't sign in to anything useful.
        await setDoc(doc(db, 'parents', uid), {
          id: uid,
          userId: uid,
          fullName: addUserForm.username,
          firstName,
          lastName,
          email: addUserForm.email,
          phoneNumber: '',
          linkedChildrenIds: [],
          isActive: true,
          schoolId: adminData?.schoolId || null,
          createdAt: new Date().toISOString()
        });
      }
      
      setShowAddUserModal(false);
      setAddUserForm({
        username: '',
        email: '',
        password: '',
        role: 'Teacher',
        teacherType: 'regular',
        specialtySubjects: []
      });
      loadUsers();
    } catch (error) {
      console.error('Error adding user:', error);
      setAddUserError(error.message || 'Failed to add user');
    }
  };

  const handleCancelAddUser = () => {
    setShowAddUserModal(false);
    setAddUserForm({
      username: '',
      email: '',
      password: '',
      role: 'Teacher',
      teacherType: 'regular',
      specialtySubjects: []
    });
    setAddUserError('');
  };

  const handleDeleteUser = async (user) => {
    const name = user.username || user.email || 'this user';
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) return false;
    try {
      await updateDoc(doc(db, 'users', user.id), {
        isActive: false,
        deletedAt: new Date().toISOString()
      });
      dataCacheService.invalidatePath('users');
      loadUsers();
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
      return false;
    }
  };

  const openUserDetail = async (user) => {
    setDetailUser(user);
    setDetailUserChildren([]);
    if (user.role !== 'Parent') return;
    try {
      // Resolve linked children — match linkedChildrenIds against studentId, doc id, and uuid (case-insensitive)
      let childIds = user.linkedChildrenIds || [];
      try {
        const parentDoc = await dataCacheService.getDoc(ck(`parents/${user.id}`), doc(db, 'parents', user.id));
        if (parentDoc && (parentDoc.linkedChildrenIds || []).length > 0) {
          childIds = parentDoc.linkedChildrenIds;
        }
      } catch (e) {
        console.error('🔥 AdminUsers: Error loading parent profile for', user.id, e);
      }
      const ids = childIds.map(id => String(id).toLowerCase());
      const matched = students.filter(s =>
        ids.includes(String(s.studentId || '').toLowerCase()) ||
        ids.includes(String(s.id || '').toLowerCase()) ||
        ids.includes(String(s.uuid || '').toLowerCase())
      );
      setDetailUserChildren(matched);
    } catch (error) {
      console.error('🔥 AdminUsers: Error resolving linked children:', error);
    }
  };

  const getRoleIcon = (role) => {
    const icons = {
      'Admin': '👑',
      'Teacher': '👨‍🏫',
      'Parent': '👨‍👩‍👧'
    };
    return icons[role] || '👤';
  };

  const getRoleColor = (role) => {
    const colors = {
      'Admin': '#14532d',
      'Teacher': '#22c55e',
      'Parent': '#22c55e'
    };
    return colors[role] || '#666';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const filteredUsers = getFilteredUsers();

  const userRoleCount = (r) => users.filter(u => (u.role || '').toLowerCase() === r).length;

  // Edit Teacher Type modal — shared by the list page and the detail page
  const userEditModal = editingUser && (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Edit Teacher Type</h3>
        <div className="form-group">
          <label>Teacher Type</label>
          <select
            value={editForm.teacherType}
            onChange={(e) => setEditForm({ ...editForm, teacherType: e.target.value })}
          >
            <option value="regular">Regular (Home Classroom)</option>
            <option value="subject">Subject/Activity Teacher</option>
          </select>
        </div>
        {editForm.teacherType === 'subject' && (
          <div className="form-group">
            <label>Specialty Subject</label>
            <select
              value={editForm.specialtySubject}
              onChange={(e) => setEditForm({ ...editForm, specialtySubject: e.target.value })}
            >
              <option value="">Select Subject</option>
              {availableSubjects.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>
        )}
        <div className="modal-actions">
          <button onClick={handleCancelEdit} className="cancel-button">Cancel</button>
          <button onClick={handleSaveUser} className="save-button">Save</button>
        </div>
      </div>
    </div>
  );

  // Add User modal — shared by the list page and the detail page
  const addUserModal = showAddUserModal && (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Add New User</h3>
        {addUserError && <div className="error-message">{addUserError}</div>}
        <div className="form-group">
          <label>Username</label>
          <input
            type="text"
            value={addUserForm.username}
            onChange={(e) => setAddUserForm({ ...addUserForm, username: e.target.value })}
            placeholder="Enter username"
          />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={addUserForm.email}
            onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
            placeholder="Enter email"
          />
        </div>
        <div className="form-group">
          <label>Password</label>
          <input
            type="password"
            value={addUserForm.password}
            onChange={(e) => setAddUserForm({ ...addUserForm, password: e.target.value })}
            placeholder="Enter password"
          />
        </div>
        <div className="form-group">
          <label>Role</label>
          <select
            value={addUserForm.role}
            onChange={(e) => setAddUserForm({ ...addUserForm, role: e.target.value, teacherType: 'regular', specialtySubject: '' })}
          >
            <option value="Teacher">Teacher</option>
            <option value="Admin">Admin</option>
            <option value="Parent">Parent</option>
            <option value="Scanner">Scanner</option>
          </select>
        </div>
        {addUserForm.role === 'Teacher' && (
          <>
            <div className="form-group">
              <label>Teacher Type</label>
              <select
                value={addUserForm.teacherType}
                onChange={(e) => setAddUserForm({ ...addUserForm, teacherType: e.target.value, specialtySubjects: [] })}
              >
                <option value="regular">Regular (Home Classroom)</option>
                <option value="subject">Subject/Activity Teacher</option>
              </select>
            </div>
            {addUserForm.teacherType === 'subject' && (
              <div className="form-group">
                <label>Specialty Subjects (select one or more)</label>
                <div className="subjects-checkboxes">
                  {availableSubjects.map(subject => (
                    <label key={subject} className="subject-checkbox">
                      <input
                        type="checkbox"
                        value={subject}
                        checked={addUserForm.specialtySubjects.includes(subject)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAddUserForm({ ...addUserForm, specialtySubjects: [...addUserForm.specialtySubjects, subject] });
                          } else {
                            setAddUserForm({ ...addUserForm, specialtySubjects: addUserForm.specialtySubjects.filter(s => s !== subject) });
                          }
                        }}
                      />
                      {subject}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        <div className="modal-actions">
          <button onClick={handleCancelAddUser} className="cancel-button">Cancel</button>
          <button onClick={handleAddUser} className="save-button">Add User</button>
        </div>
      </div>
    </div>
  );

  // Full-screen user detail page — replaces the list
  if (detailUser) {
    const user = users.find(u => u.id === detailUser.id) || detailUser;
    const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || user.email || 'Unknown';
    const isActive = user.isActive !== false;
    return (
      <div className="admin-users-view">
        <div className="detail-page">
          <button className="detail-back-button" onClick={() => setDetailUser(null)}>
            ◄ Back to Users
          </button>

          <div className="detail-hero">
            <div className="detail-avatar" style={{ background: `${getRoleColor(user.role)}20`, color: getRoleColor(user.role) }}>
              {getRoleIcon(user.role)}
            </div>
            <div className="detail-hero-info">
              <h2>{userName}</h2>
              <p className="detail-subtitle">{user.email || 'No email'}</p>
              <div className="detail-badges">
                <span className="detail-badge" style={{ background: `${getRoleColor(user.role)}20`, color: getRoleColor(user.role) }}>
                  {user.role || 'User'}
                </span>
                <span className={`detail-badge ${isActive ? 'green' : 'red'}`}>{isActive ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
            <div className="detail-hero-actions">
              {user.role === 'Teacher' && (
                <button className="detail-btn detail-btn-edit" onClick={() => handleEditUser(user)}>
                  Edit
                </button>
              )}
              <button
                className="detail-btn detail-btn-delete"
                onClick={async () => { if (await handleDeleteUser(user)) setDetailUser(null); }}
              >
                Delete
              </button>
            </div>
          </div>

          <div className="detail-grid">
            <div className="detail-card">
              <h3>Account Information</h3>
              <div className="detail-field">
                <span className="field-label">Name</span>
                <span className="field-value">{userName}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Email</span>
                <span className="field-value">{user.email || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Username</span>
                <span className="field-value">{user.username || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Role</span>
                <span className="field-value">{user.role || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Phone</span>
                <span className="field-value">{user.phoneNumber || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Status</span>
                <span className="field-value">{isActive ? 'Active' : 'Inactive'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Created</span>
                <span className="field-value">{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">User ID</span>
                <span className="field-value">{user.id}</span>
              </div>
            </div>

            {user.role === 'Teacher' && (
              <div className="detail-card">
                <h3>Teacher Details</h3>
                <div className="detail-field">
                  <span className="field-label">Teacher Type</span>
                  <span className="field-value">{user.teacherType === 'subject' ? 'Subject Teacher' : 'Class Teacher'}</span>
                </div>
                <div className="detail-field">
                  <span className="field-label">Specialty Subject</span>
                  <span className="field-value">{user.specialtySubject || 'N/A'}</span>
                </div>
              </div>
            )}

            {user.role === 'Teacher' && (user.specialtySubjects || []).length > 0 && (
              <div className="detail-card">
                <h3>Specialty Subjects</h3>
                <div className="detail-chips">
                  {user.specialtySubjects.map(subject => (
                    <span key={subject} className="detail-chip">{subject}</span>
                  ))}
                </div>
              </div>
            )}

            {user.role === 'Parent' && (
              <div className="detail-card">
                <h3>Linked Children ({detailUserChildren.length})</h3>
                {detailUserChildren.length === 0 ? (
                  <p className="detail-empty">No linked children</p>
                ) : (
                  <div className="detail-list">
                    {detailUserChildren.map(child => (
                      <div key={child.id} className="detail-list-item">
                        <span className="item-name">{child.fullName || `${child.firstName || ''} ${child.lastName || ''}`.trim() || 'Unknown'}</span>
                        <span className="item-meta">{child.studentId || ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {userEditModal}
        {addUserModal}
      </div>
    );
  }

  return (
    <div className="admin-users-view">
      <div className="view-header">
        <h2>Users</h2>
        <button onClick={() => setShowAddUserModal(true)} className="add-user-button">
          + Add User
        </button>
      </div>
      <StatCards items={[['Parents',userRoleCount('parent')],['Teachers',userRoleCount('teacher')],['Scanners',userRoleCount('scanner')],['Admins',userRoleCount('admin')],['Total',users.length]]} charts={[{title:'Users by Role',type:'pie',data:['Parent','Teacher','Scanner','Admin'].map(r => ({name:r,value:userRoleCount(r.toLowerCase())}))}]} />

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search users..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      <div className="role-filters">
        <button
          className={`role-filter-btn ${roleFilter === 'All' ? 'active' : ''}`}
          onClick={() => setRoleFilter('All')}
        >
          All ({users.length})
        </button>
        <button
          className={`role-filter-btn ${roleFilter === 'Admin' ? 'active' : ''}`}
          onClick={() => setRoleFilter('Admin')}
        >
          Admin ({users.filter(u => u.role === 'Admin').length})
        </button>
        <button
          className={`role-filter-btn ${roleFilter === 'Teacher' ? 'active' : ''}`}
          onClick={() => setRoleFilter('Teacher')}
        >
          Teacher ({users.filter(u => u.role === 'Teacher').length})
        </button>
        <button
          className={`role-filter-btn ${roleFilter === 'Parent' ? 'active' : ''}`}
          onClick={() => setRoleFilter('Parent')}
        >
          Parent ({users.filter(u => u.role === 'Parent').length})
        </button>
      </div>

      <p className="count-text">Users: {filteredUsers.length}</p>

      {filteredUsers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👤</div>
          <h3>No Users</h3>
          <p>No users found</p>
        </div>
      ) : (
        <div className="users-list">
          {filteredUsers.map(user => (
            <div key={user.id} className="user-card" onClick={() => openUserDetail(user)} style={{ cursor: 'pointer' }}>
              <div className="user-avatar" style={{ background: `${getRoleColor(user.role)}20` }}>
                <span style={{ color: getRoleColor(user.role) }}>
                  {getRoleIcon(user.role)}
                </span>
              </div>
              <div className="user-info">
                <h4>{user.username}</h4>
                <p>{user.email}</p>
                <span className="role-badge" style={{ background: `${getRoleColor(user.role)}20`, color: getRoleColor(user.role) }}>
                  {user.role}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {userEditModal}

      {addUserModal}
    </div>
  );
}

export default AdminUsersView;
